import React from "react";
import type { PlateElementProps, PlateLeafProps } from "platejs/react";
import { PlateElement, PlateLeaf, useEditorRef, useReadOnly } from "platejs/react";
import { TablePlugin } from "@platejs/table/react";
import {
  ArrowDownToLine,
  ArrowUpToLine,
  ArrowLeftToLine,
  ArrowRightToLine,
  MoreHorizontal,
  Trash2,
} from "lucide-react";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "../../../../altzero_plugins/foundation/altzero_base/base/components/ui/context-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../../altzero_plugins/foundation/altzero_base/base/components/ui/dropdown-menu";

// Pull text-align + custom font-size from the node so renderers don't
// need to repeat the same extraction logic.
// Reads `textAlign` (TextAlignPlugin) with `align` as fallback (legacy nodes).
const blockStyle = (el: PlateElementProps["element"]): React.CSSProperties => {
  const e = el as { textAlign?: string; align?: string };
  const ta = e.textAlign ?? e.align;
  return ta ? { textAlign: ta as React.CSSProperties["textAlign"] } : {};
};

// Brand colors are injected via CSS variables on an ancestor (see BrandedProposalEditor).
// Fallbacks match the previous hardcoded gray-900/gray-700 so unbranded callers
// of these renderers (template editor) render identically to before.
const brandPrimary    = 'var(--brand-primary, #111827)';
const brandSecondary  = 'var(--brand-secondary, #6b7280)';
const brandFontColor  = 'var(--brand-font-color, #111827)';

export const ParagraphElement = (props: PlateElementProps) => {
  // @platejs/list represents a bullet/number as a `p` carrying `listStyleType`
  // (+ `indent`, applied by the indent plugin as marginLeft). The plugin wraps
  // the children in an inline <ul style="padding:0"> whose marker draws OUTSIDE
  // that zero-padding box — i.e. flush with the paragraph edge. Adding
  // paddingLeft to the list-item paragraph shifts that marker inward so bullets
  // sit indented under the surrounding heading/paragraph, matching the
  // generation-page preview.
  const isListItem = !!(props.element as { listStyleType?: string }).listStyleType;
  return (
    <PlateElement
      {...props}
      as="p"
      className="my-2"
      style={{
        color: brandFontColor,
        lineHeight: 1.75,
        ...(isListItem ? { paddingLeft: '1.5rem' } : {}),
        ...blockStyle(props.element),
      }}
    >
      {props.children}
    </PlateElement>
  );
};

export const Heading1Element = (props: PlateElementProps) => (
  <PlateElement
    {...props}
    as="h1"
    className="mt-6 mb-3 text-3xl font-bold tracking-tight pb-2"
    style={{ color: brandPrimary, borderBottom: `2px solid ${brandPrimary}`, ...blockStyle(props.element) }}
  >
    {props.children}
  </PlateElement>
);

export const Heading2Element = (props: PlateElementProps) => (
  <PlateElement
    {...props}
    as="h2"
    className="mt-5 mb-2 text-2xl font-semibold tracking-tight pb-1"
    style={{ color: brandSecondary, borderBottom: `1px solid ${brandSecondary}`, ...blockStyle(props.element) }}
  >
    {props.children}
  </PlateElement>
);

export const Heading3Element = (props: PlateElementProps) => (
  <PlateElement
    {...props}
    as="h3"
    className="mt-4 mb-2 text-xl font-semibold"
    style={{ color: brandSecondary, ...blockStyle(props.element) }}
  >
    {props.children}
  </PlateElement>
);

export const BlockquoteElement = (props: PlateElementProps) => (
  <PlateElement
    {...props}
    as="blockquote"
    className="my-3 border-l-4 pl-4 italic"
    style={{ borderColor: brandSecondary, color: brandFontColor, ...blockStyle(props.element) }}
  >
    {props.children}
  </PlateElement>
);

// NOTE: With @platejs/list (indent-based lists) these ul/ol/li renderers are
// effectively unused — a bullet/number is a `p` node carrying `listStyleType` +
// `indent`, and the marker is drawn by the plugin's own inline <ul>/<li>
// wrapper. List indentation is handled in ParagraphElement instead (see the
// `isListItem` branch). These remain only for any legacy classic-list nodes.
export const UnorderedListElement = (props: PlateElementProps) => (
  <PlateElement {...props} as="ul" className="my-2 ml-6 list-disc" style={{ color: brandFontColor }}>
    {props.children}
  </PlateElement>
);

export const OrderedListElement = (props: PlateElementProps) => (
  <PlateElement {...props} as="ol" className="my-2 ml-6 list-decimal" style={{ color: brandFontColor }}>
    {props.children}
  </PlateElement>
);

export const ListItemElement = (props: PlateElementProps) => (
  <PlateElement {...props} as="li" className="my-0.5 leading-7">
    {props.children}
  </PlateElement>
);

export const ListItemContentElement = (props: PlateElementProps) => (
  <PlateElement {...props} as="span" className="leading-7">
    {props.children}
  </PlateElement>
);

export const CodeBlockElement = (props: PlateElementProps) => (
  <PlateElement
    {...props}
    as="pre"
    className="my-3 rounded bg-gray-100 p-3 text-sm font-mono overflow-x-auto"
  >
    <code>{props.children}</code>
  </PlateElement>
);

export const HrElement = (props: PlateElementProps) => (
  <PlateElement {...props} as="div" className="my-4">
    <hr contentEditable={false} className="border-gray-300" />
    {props.children}
  </PlateElement>
);

export const ImageElement = (props: PlateElementProps) => {
  const url = (props.element as { url?: string }).url;
  const alt = (props.element as { alt?: string }).alt ?? "";
  const el = props.element as { textAlign?: string; align?: string };
  const isCentered = (el.textAlign ?? el.align) === "center";
  return (
    <PlateElement {...props} as="div" className="my-3">
      <div
        contentEditable={false}
        style={isCentered ? { display: "flex", justifyContent: "center" } : {}}
      >
        {url ? (
          <img
            src={url}
            alt={alt}
            style={isCentered ? { maxHeight: "100px", width: "auto" } : { maxWidth: "100%" }}
          />
        ) : (
          <div className="text-gray-400 italic">(image)</div>
        )}
      </div>
      {props.children}
    </PlateElement>
  );
};

// Shape of the @platejs/table transforms we use here. Narrowed locally
// because Plate's TablePlugin type isn't structurally exported.
type TableTransforms = {
  insert?: {
    tableRow?: (opts?: { before?: boolean }) => void;
    tableColumn?: (opts?: { before?: boolean }) => void;
  };
  remove?: {
    tableRow?: () => void;
    tableColumn?: () => void;
    table?: () => void;
  };
};

// Action list used by both the right-click context menu and the floating
// "table actions" dropdown — defining once keeps the two surfaces in sync.
type TableAction = {
  label: string;
  icon: typeof Trash2;
  run: (tf: TableTransforms) => void;
  separatorBefore?: boolean;
  destructive?: boolean;
};

const TABLE_ACTIONS: ReadonlyArray<TableAction> = [
  { label: "Insert row above",    icon: ArrowUpToLine,    run: (tf) => tf.insert?.tableRow?.({ before: true }) },
  { label: "Insert row below",    icon: ArrowDownToLine,  run: (tf) => tf.insert?.tableRow?.() },
  { label: "Insert column left",  icon: ArrowLeftToLine,  run: (tf) => tf.insert?.tableColumn?.({ before: true }) },
  { label: "Insert column right", icon: ArrowRightToLine, run: (tf) => tf.insert?.tableColumn?.() },
  { label: "Delete row",          icon: Trash2,           run: (tf) => tf.remove?.tableRow?.(),    separatorBefore: true },
  { label: "Delete column",       icon: Trash2,           run: (tf) => tf.remove?.tableColumn?.() },
  { label: "Delete table",        icon: Trash2,           run: (tf) => tf.remove?.table?.(),       separatorBefore: true, destructive: true },
];

export const TableElement = (props: PlateElementProps) => {
  // Do NOT wrap children in <tbody> — Slate's DOM reconciler uses
  // data-slate-node attributes to map DOM nodes back to Slate nodes.
  // An extra <tbody> without those attributes breaks click-to-focus and
  // cursor placement inside cells. Plate handles the table structure itself.
  const editor = useEditorRef();
  const readOnly = useReadOnly();

  // No margin on the table itself — the wrapper applies it so the floating
  // action button can be positioned relative to the table edge.
  const table = (
    <PlateElement
      {...props}
      as="table"
      className="w-full border-collapse table-fixed"
    >
      {props.children}
    </PlateElement>
  );

  if (readOnly) {
    return <div className="my-3">{table}</div>;
  }

  const runTableAction = (action: (tf: TableTransforms) => void) => {
    try {
      const tf = editor.getTransforms(TablePlugin) as TableTransforms;
      action(tf);
    } catch (e) {
      console.warn("[TableElement] Table action failed:", e);
    }
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        {/* `group/table` scopes the hover/focus class so nested tables don't
            cross-trigger each other. `relative` anchors the floating button. */}
        <div className="group/table relative my-3">
          {table}
          {/* Floating "table actions" button — visible on hover or when the
              cursor is inside the table. contentEditable=false keeps Slate
              from trying to place the caret in this non-text region. */}
          <div
            contentEditable={false}
            className="absolute -top-3 right-1 opacity-0 transition-opacity duration-150 group-hover/table:opacity-100 group-focus-within/table:opacity-100"
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="Table actions"
                  title="Table actions"
                  className="flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-300"
                  // Prevent the button from stealing the editor selection on
                  // mousedown — the dropdown still opens on click, and the
                  // table actions need a selection inside the table to work.
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <MoreHorizontal className="size-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {TABLE_ACTIONS.map((a) => {
                  const Icon = a.icon;
                  return (
                    <React.Fragment key={a.label}>
                      {a.separatorBefore && <DropdownMenuSeparator />}
                      <DropdownMenuItem
                        onSelect={() => runTableAction(a.run)}
                        className={a.destructive ? "text-red-600 focus:bg-red-50 focus:text-red-600" : undefined}
                      >
                        <Icon className="mr-2 size-4" />
                        {a.label}
                      </DropdownMenuItem>
                    </React.Fragment>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-56">
        {TABLE_ACTIONS.map((a) => {
          const Icon = a.icon;
          return (
            <React.Fragment key={a.label}>
              {a.separatorBefore && <ContextMenuSeparator />}
              <ContextMenuItem
                onSelect={() => runTableAction(a.run)}
                className={a.destructive ? "text-red-600 focus:bg-red-50 focus:text-red-600" : undefined}
              >
                <Icon className="mr-2 size-4" />
                {a.label}
              </ContextMenuItem>
            </React.Fragment>
          );
        })}
      </ContextMenuContent>
    </ContextMenu>
  );
};

export const TableRowElement = (props: PlateElementProps) => (
  <PlateElement {...props} as="tr">
    {props.children}
  </PlateElement>
);

export const TableCellElement = (props: PlateElementProps) => (
  <PlateElement
    {...props}
    as="td"
    className="border border-gray-200 px-3 py-2 align-top min-w-[60px]"
    style={{ color: brandFontColor }}
  >
    {props.children}
  </PlateElement>
);

export const TableHeaderElement = (props: PlateElementProps) => (
  <PlateElement
    {...props}
    as="th"
    className="border border-gray-200 px-3 py-2 font-semibold text-left min-w-[60px]"
    style={{ color: brandPrimary, backgroundColor: 'var(--brand-primary-subtle, #f3f4f6)' }}
  >
    {props.children}
  </PlateElement>
);

export const BoldLeaf = (props: PlateLeafProps) => (
  <PlateLeaf {...props} as="strong" style={{ color: brandPrimary, fontWeight: 600 }}>
    {props.children}
  </PlateLeaf>
);

export const ItalicLeaf = (props: PlateLeafProps) => (
  <PlateLeaf {...props} as="em">
    {props.children}
  </PlateLeaf>
);

export const UnderlineLeaf = (props: PlateLeafProps) => (
  <PlateLeaf {...props} as="u">
    {props.children}
  </PlateLeaf>
);

export const StrikethroughLeaf = (props: PlateLeafProps) => (
  <PlateLeaf {...props} as="s">
    {props.children}
  </PlateLeaf>
);

export const CodeLeaf = (props: PlateLeafProps) => (
  <PlateLeaf
    {...props}
    as="code"
    className="rounded bg-gray-100 px-1 py-0.5 font-mono text-sm"
  >
    {props.children}
  </PlateLeaf>
);

export const FontSizeLeaf = (props: PlateLeafProps) => {
  const fontSize = (props.leaf as { fontSize?: string }).fontSize;
  return (
    <PlateLeaf {...props} style={fontSize ? { fontSize } : {}}>
      {props.children}
    </PlateLeaf>
  );
};

export const FontColorLeaf = (props: PlateLeafProps) => {
  const color = (props.leaf as { color?: string }).color;
  return (
    <PlateLeaf {...props} style={color ? { color } : {}}>
      {props.children}
    </PlateLeaf>
  );
};

export const proposalNodeComponents = {
  p: ParagraphElement,
  h1: Heading1Element,
  h2: Heading2Element,
  h3: Heading3Element,
  blockquote: BlockquoteElement,
  ul: UnorderedListElement,
  ol: OrderedListElement,
  li: ListItemElement,
  lic: ListItemContentElement,
  code_block: CodeBlockElement,
  hr: HrElement,
  img: ImageElement,
  table: TableElement,
  tr: TableRowElement,
  td: TableCellElement,
  th: TableHeaderElement,
  bold: BoldLeaf,
  italic: ItalicLeaf,
  underline: UnderlineLeaf,
  strikethrough: StrikethroughLeaf,
  code: CodeLeaf,
  fontSize: FontSizeLeaf,
  color: FontColorLeaf,
};
